import { z } from 'zod';
import { deliveryEstimate } from '../crew/schema/deliveryEstimate.js';
import { CountryCode } from '../enums/countries.js';
import { enumToZodLiteralUnion, helperSchema, optionalizeString, } from '../zodExtensions.js';
import { CartChangeDetectionMethod, CheckoutPlusAction, CheckoutPlusInsertionStrategy, CheckoutPlusRefreshMethod, CorsoCartAttribute, CorsoLineItemProperty, ShopifyExtensionHandle, WIDGET_DEFAULTS, } from './shared.js';
// ? these schemas are a "type-first" approach to zod schemas, due to the limitation of what can and can't be ran on Shopify extensions
// ---------- SHIPPING CODE -----------------------------------
export const corsoShippingCodeSchema = z.object({
    rateTitle: z.string().optional(),
    rateId: z.number().optional(),
    rateKind: z.union([z.literal('plus'), z.literal('non-plus')]),
    shippingDiscount: z
        .object({
        message: z.string().optional(),
        amount: z.number(),
        type: z.union([z.literal('percentage'), z.literal('fixedAmount')]),
        isCheckboxApplicationEnabled: z.boolean(),
    })
        .optional(),
    shipProtectUnitId: z.number().optional(),
    deliveryEstimate: deliveryEstimate.optional(),
    pricingDetail: z
        .object({
        shippingPrice: z.number(),
        plusPrice: z.number(),
        currency: z.string(),
    })
        .optional(),
});
// -------------- WIDGET CONFIG --------------------------------
const INSURANCE_TEXT_MAX = 128;
const modalSchema = z.object({
    imageUrl: z.string().url(),
    title: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
    description: z.string().nullable(),
    benefits: z.array(z.object({
        title: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
        description: helperSchema.insuranceString(),
    })),
});
export const shippingPlusConfigSchema = z.object({
    // -- General Settings
    isEnabled: z.boolean().default(false).optional(),
    isRateTableCacheEnabled: z.boolean().default(false).optional(),
    enabledLineItemProperties: z
        .array(z.enum([
        CorsoLineItemProperty.appliedGiftCardCodes,
        CorsoLineItemProperty.customer,
    ]))
        .optional(),
    isWidgetDebugEnabled: z.boolean().default(false).optional(),
    isFreeShippingOptInEnabled: z.boolean().default(false).optional(),
    isUpdateExistingDiscountsEnabled: z.boolean().default(false).optional(),
    backupRateIds: z.array(z.number()).default([]).optional(),
    enabledCartAttributes: z
        .array(enumToZodLiteralUnion(CorsoCartAttribute))
        .default([])
        .optional(),
    // -- Delivery Customization Settings
    deliveryCustomization: z
        .object({
        isEnabled: z.boolean().default(false).optional(),
        isReorderShippingLinesEnabled: z.boolean().default(false).optional(),
        hideRateNames: z.array(z.string()).default([]).optional(),
        hideRateNamesOptOut: z.array(z.string()).default([]).optional(),
    })
        .optional(),
    intelligems: z
        .object({
        isEnabled: z.boolean().default(false).optional(),
        lineItemAttributeKey: z.string().default('_igTestGroup').optional(),
    })
        .optional(),
    // -- UI settings for the Widget + Modal-------
    label: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
    freeShippingLabel: helperSchema.nullifiedInsuranceString(INSURANCE_TEXT_MAX),
    freeShippingDescription: helperSchema.nullifiedInsuranceString(),
    description: helperSchema.nullifiedInsuranceString(),
});
const selectors = z.string().array();
const requiredSelectors = selectors.min(1);
const checkoutPlusInsertionStrategy = enumToZodLiteralUnion(CheckoutPlusInsertionStrategy);
const checkoutPlusElement = z.object({
    selectors: requiredSelectors,
    title: z.string(),
    description: helperSchema.nullifiedInsuranceString(128),
    insertionStrategy: checkoutPlusInsertionStrategy,
    imgUrl: optionalizeString(z.url()),
    imgHtmlContent: z.string().default(''),
    kind: z.union([z.literal('altCheckoutButton'), z.literal('checkoutInfo')]),
});
const SHOP_NOW_CHECKOUT_BUTTON_TEXT = WIDGET_DEFAULTS.checkoutPlus.shopNow.checkoutButtonText;
const checkoutPlusConfigSchema = z.object({
    isEnabled: z.boolean(),
    cartRetrievalKey: z.string().default('cart'),
    scriptTagNote: z.string().optional(),
    isDirectCheckoutEnabled: z.boolean().default(false).optional(),
    removeProtectionProductOnPageLoad: z.boolean().default(true),
    shouldProtectCompanyCarts: z.boolean().default(true),
    shouldQueryShadowDom: z.boolean().default(false),
    cartRefreshMethod: enumToZodLiteralUnion(CheckoutPlusRefreshMethod).default('reloadPage'),
    disabledUrls: z.array(z.string()).default([]),
    disabledCustomerTags: z.array(z.string()).default([]),
    cloneOmitAttributes: z.array(z.string()).default([]),
    productCheckoutExtensionTitle: z.string().default('Delivery Guarantee'),
    productCheckoutExtensionDescription: z
        .string()
        .default('For lost, damaged, or stolen packages'),
    checkoutSelectors: requiredSelectors.default(['button[name="checkout"]']),
    checkoutButtonText: z.string().trim().optional(), // TODO consider rename to checkoutContent or checkoutTemplate
    areSubscriptionsEnabled: z.boolean().default(false),
    hideWidgetSkus: z.array(z.string()).default([]),
    isQueryAjaxCartApiEnabled: z.boolean().default(true),
    cartChangeDetectionMethod: enumToZodLiteralUnion(CartChangeDetectionMethod).default('cartWatcher'),
    displayCountryCodes: z
        .array(enumToZodLiteralUnion(CountryCode))
        .min(1)
        .default(['US']),
    // TODO keep in sync with appropriate defaults in `apps/corso-checkout-plus` and `WIDGET_DEFAULTS` in `packages/corso-types/src/corso-shopify-extensions/shared.ts`
    cartSelectors: requiredSelectors.default([
        'cart-items',
        'cart-drawer',
        '#cart',
    ]),
    checkoutAction: enumToZodLiteralUnion(CheckoutPlusAction),
    customCss: z.string().default('').optional(),
    elements: z.array(checkoutPlusElement).refine((elements) => {
        const kinds = new Set(elements.map((e) => e.kind));
        return kinds.size === checkoutPlusElement.shape.kind.options.length; // all kinds must be present
    }, { message: 'At least one of each element type is required.' }),
    shopNow: z
        .object({
        placement: z
            .enum(['bottom left', 'bottom right'])
            .default('bottom left')
            .optional(),
        hideSelectors: selectors
            .default(['shopify-accelerated-checkout'])
            .optional(), // marked optional, as it may not exist in some configs
        checkoutButtonText: z
            .string()
            .trim()
            .transform((text) => text || SHOP_NOW_CHECKOUT_BUTTON_TEXT) // an empty button renders blank on the storefront
            .default(SHOP_NOW_CHECKOUT_BUTTON_TEXT)
            .optional(), // marked as optional, as it may not exist in some configs
    })
        .optional(),
});
export const externalStorefrontConfigSchema = z.object({
    storefrontId: z.string(),
    baseApiUrl: z.string(),
    checkoutPlusUrl: z.string(),
    defaultProtectSetId: z.number(),
});
const checkoutPlusProductExtension = z.object({
    isDefaultOptIn: z.boolean().default(false).optional(),
});
export const widgetConfigSchema = z.object({
    externalStorefrontId: z.string(),
    modal: modalSchema,
    shippingPlus: shippingPlusConfigSchema,
    checkoutPlus: checkoutPlusConfigSchema,
    checkoutExtensionProduct: checkoutPlusProductExtension.optional(),
});
export function decodeCorsoShippingCodeWithZod(code) {
    try {
        if (!code)
            return null;
        const parsed = corsoShippingCodeSchema.safeParse(JSON.parse(code));
        if (parsed.success)
            return parsed.data;
    }
    catch {
        /* empty */
    }
    return null;
}
// ---------- CART LINE PROPERTIES (NEW APPROACH) -----------------------------
export const corsoLineItemPropertySchema = z
    .object({
    [CorsoLineItemProperty.appliedGiftCardCodes]: z.string().optional(),
    [CorsoLineItemProperty.customer]: z.string().optional(),
})
    .optional();
export const extensionHeartbeatSchema = z.object({
    appHandle: z.enum(ShopifyExtensionHandle),
    location: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
//# sourceMappingURL=schema.js.map